import { ApplicationCapabilityDefinition } from "classes/models/capabilities/application-capability-definition.model";
import { UserCounts } from "classes/models/userCounts/user-counts.model";
import { UserCountsService } from "classes/models/userCounts/user-counts.service";
import { Subscription } from "rxjs";
import { DBFactory } from "~/classes/database/db_factory";
import { CapabilitiesService } from "~/classes/models/capabilities/capabilities.service";
import { UserCapabilities } from "~/classes/models/capabilities/user-capabilities.model";
import { UserPackage } from "~/classes/models/capabilities/user-package.model";
import { ConsumablesService } from "~/classes/models/consumables/consumables.service";
import { UserConsumables } from "~/classes/models/consumables/user-consumable.model";
import { OrganizationSummary } from "~/classes/models/organizations/organization-summary.model";
import { OrganizationsService } from "~/classes/models/organizations/organizations.service";

export const useCapabilitiesService = (userId: string) =>
  defineStore(`/capabilities/service/${userId}`, () => {
    const userCapabilities = ref<UserCapabilities | undefined>();
    const userCounts = ref<UserCounts | undefined>();
    const userPackages = ref<UserPackage[] | undefined>();
    const userConsumables = ref<UserConsumables | undefined>();
    const applicationCapabilitiesDefinition = ref<
      ApplicationCapabilityDefinition | undefined
    >();
    const organizationSummary = ref<OrganizationSummary | undefined>();

    const applicationDefinitionSubscription = ref<Subscription | undefined>();
    const organizationSummarySubscription = ref<Subscription | undefined>();
    const userCountsSubscription = ref<Subscription | undefined>();
    const userConsumablesSubscription = ref<Subscription | undefined>();

    const userClassroomsData = useUserClassroomsDataStore();
    const { classrooms } = storeToRefs(userClassroomsData);

    const isInitialized = ref(false);
    const isRequestedInitialized = ref(false);

    const initialize = async () => {
      if (isRequestedInitialized.value) return;

      isRequestedInitialized.value = true;

      await initUserCapabilities();

      await initializeUserCounts();

      await initUserConsumabled();

      isInitialized.value = true;
    };

    watch(
      [
        userCounts,
        userPackages,
        userConsumables,
        classrooms,
        organizationSummary,
      ],
      () => {
        if (userCapabilities.value == undefined) return;

        userCapabilities.value = new UserCapabilities({
          userId: userId,
          applicationCapabilitiesDefinition:
            applicationCapabilitiesDefinition.value ??
            userCapabilities.value!.applicationCapabilitiesDefinition,
          userPackageVersion: userCapabilities.value!.userPackageVersion,
          userPackages:
            userPackages.value ?? userCapabilities.value!.userPackages,
          userConsumables:
            userCapabilities.value!.userConsumables ?? userConsumables.value,
          userCounts: userCounts.value ?? userCapabilities.value!.userCounts,
          // The shared classroom store belongs to the current session. Admin
          // views must retain the selected user's classrooms loaded at initialization.
          userClassrooms:
            userId === useCurrentUID()
              ? classrooms.value ?? userCapabilities.value!.userClassrooms
              : userCapabilities.value!.userClassrooms,
          organizationSummary:
            organizationSummary.value ??
            userCapabilities.value!.organizationSummary,
        });
      }
    );

    const initUserCapabilities = async () => {
      userCapabilities.value = await UserCapabilities.initialize(userId);

      const db = DBFactory.createDatabase();
      const data = await db.get({
        collection: "users",
        path: `/users/${userId}`,
      });

      const organizationId =
        userCapabilities.value?.userPackageVersion.organizationId ??
        data.organizationId;

      if (organizationId != undefined) {
        initOrganizationSummary(organizationId);
      }

      applicationDefinitionSubscription.value?.unsubscribe();

      applicationDefinitionSubscription.value =
        CapabilitiesService.streamApplicationDefinitionAndActiveUserPackages(
          userId
        ).subscribe((data) => {
          applicationCapabilitiesDefinition.value = data.applicationDefinition;
          userPackages.value = data.userPackages;
        });
    };

    const initOrganizationSummary = async (organizationId: string) => {
      organizationSummarySubscription.value?.unsubscribe();

      organizationSummarySubscription.value =
        OrganizationsService.streamOrganizationSummary({
          organizationId,
        }).subscribe((data) => {
          organizationSummary.value = data;
        });
    };

    const initializeUserCounts = async () => {
      if (process.client) {
        userCountsSubscription.value?.unsubscribe();
        userCountsSubscription.value = UserCountsService.streamUserCounts(
          userId
        ).subscribe((data) => {
          userCounts.value = data;
        });
      }
    };

    const initUserConsumabled = () => {
      if (process.client) {
        userConsumablesSubscription.value?.unsubscribe();
        userConsumablesSubscription.value =
          ConsumablesService.streamUserConsumables(userId).subscribe((data) => {
            userConsumables.value = data;
          });
      }
    };

    const appUserDataStore = useAppUserDataStore();
    const { isUserAdmin, isUserSystemAdmin } = storeToRefs(appUserDataStore);

    const canGradeDocument = computed(() => {
      return (documentId: string) => {
        // Basic checks
        if (isUserAdmin.value || isUserSystemAdmin.value) return true;

        return userCapabilities.value?.canGradeDocument(documentId) ?? true;
      };
    });

    const numAllowedClassrooms = computed(() => {
      if (userCapabilities.value == undefined) return 0;

      return userCapabilities.value.numAllowedClassrooms;
    });

    const numActiveClassrooms = computed(() => {
      if (userCapabilities.value == undefined) return 0;

      return userCapabilities.value.numActiveClassrooms;
    });

    const userHasOtherClassrooms = computed(() => {
      if (userCapabilities.value == undefined) return false;

      return userCapabilities.value.userHasOtherClassrooms;
    });

    const organizationHasEnoughClassrooms = computed(() => {
      if (userCapabilities.value == undefined) return false;

      return userCapabilities.value.organizationHasEnoughClassrooms;
    });

    const hasEnoughClassroomLicenses = computed(() => {
      if (isUserAdmin.value || isUserSystemAdmin.value) return true;

      if (userCapabilities.value == undefined) return true;

      return userCapabilities.value.hasEnoughClassroomLicenses;
    });

    const canUserAddClassroom = computed(() => {
      if (isUserAdmin.value || isUserSystemAdmin.value) return true;

      if (userCapabilities.value == undefined) return true;

      return userCapabilities.value.canUserAddClassroom;
    });

    const canOrganizationAddClassroom = computed(() => {
      if (isUserAdmin.value || isUserSystemAdmin.value) return true;

      if (userCapabilities.value == undefined) return true;

      return userCapabilities.value.canOrganizationAddClassroom;
    });

    const hasSubscription = computed(() => {
      if (isUserAdmin.value || isUserSystemAdmin.value) return true;

      if (userCapabilities.value == undefined) return false;

      return userCapabilities.value.hasSubscription;
    });

    return {
      isInitialized,
      initialize,
      userCapabilities,
      canGradeDocument,
      hasEnoughClassroomLicenses,
      canUserAddClassroom,
      numActiveClassrooms,
      numAllowedClassrooms,
      hasSubscription,
      userHasOtherClassrooms,
      organizationHasEnoughClassrooms,
      canOrganizationAddClassroom,
    };
  });
